import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { PageHeader } from "@/components/bespoke";
import { ApiError, approvalDetailQuery } from "@/lib/api";
import { useCurrentProject } from "@/lib/project";
import { AskButton } from "@/components/ask-zot";
import { ApprovalThread } from "./inbox-thread";
import { ApprovalStateBadge } from "@/components/inbox-badge";
import { formatDateTime } from "@/lib/format";

function DetailError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (error instanceof ApiError && error.status === 403) {
    return (
      <ForbiddenState
        title="You do not have access to this project"
        description="Ask an owner to invite you. Nothing from the project is shown."
      />
    );
  }
  const apiError = error instanceof ApiError ? error : null;
  return (
    <ErrorState
      title="Approval could not be loaded"
      description={apiError?.problem?.detail ?? "The request could not be completed."}
      requestId={apiError?.requestId ?? "unknown"}
      onRetry={onRetry}
    />
  );
}

/**
 * The approval decision page: the same working thread the inbox shows, so a shared link and
 * the queue never diverge. The rationale stays labelled unverified, and the author of the
 * change never decides their own.
 */
export function ApprovalDetailPage() {
  const { project, loaded } = useCurrentProject();
  const { approvalId } = useParams();
  const detail = useQuery({
    ...approvalDetailQuery(project?.id ?? "", approvalId ?? ""),
    enabled: loaded && project !== null && approvalId !== undefined,
  });

  const heading = (title: string) => (
    <PageHeader
      title={title}
      description={
        <p className="text-[0.8125rem] text-muted-foreground">
          <Link
            to="/approvals"
            className="inline-flex min-h-11 items-center text-accent-text underline-offset-4 hover:underline"
          >
            Approvals
          </Link>
          {" / "}
          {title}
        </p>
      }
    />
  );

  if (!loaded || (project !== null && approvalId !== undefined && detail.isPending)) {
    return (
      <>
        {heading("Approval")}
        <LoadingState label="Loading approval" rows={5} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        {heading("Approval")}
        <EmptyState
          title="No projects yet"
          description="Create your first project. Its approvals appear here."
        />
      </>
    );
  }

  if (detail.isError || !detail.data) {
    if (!detail.isError) {
      return (
        <>
          {heading("Approval")}
          <LoadingState label="Loading approval" rows={5} />
        </>
      );
    }
    return (
      <>
        {heading("Approval")}
        <DetailError error={detail.error} onRetry={() => void detail.refetch()} />
      </>
    );
  }

  const entry = detail.data;
  if (entry === null) {
    return (
      <>
        {heading("Approval")}
        <EmptyState
          title="Approval not found"
          description="It may have been decided elsewhere, or the link is wrong."
          action={{ label: "Back to approvals" }}
        />
      </>
    );
  }

  return (
    <>
      {heading(entry.title)}
      <div className="flex flex-wrap items-center gap-2">
        <ApprovalStateBadge state={entry.status} />
        <span className="text-[0.8125rem] text-muted-foreground">
          Requested {formatDateTime(entry.requestedAt)}
          {entry.expiresAt ? ` · expires ${formatDateTime(entry.expiresAt)}` : ""}
        </span>
        <AskButton kind="approval" id={entry.id} label={entry.title} />
      </div>
      <ApprovalThread projectId={project.id} entry={entry} />
    </>
  );
}
